import { useNavigate } from 'react-router-dom';
import {
  Dropdown, DropdownTrigger, DropdownMenu, DropdownItem, Badge, Button,
} from '@heroui/react';
import { Bell, CheckCheck } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useNotifications, useMarkRead, useMarkAllRead, type NotificationDto } from '@/api/notifications';

export default function NotificationBell() {
  const navigate = useNavigate();
  const { data } = useNotifications();
  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();

  const items = data?.items ?? [];
  const unread = data?.unread_count ?? 0;

  const handleClick = (n: NotificationDto) => {
    if (!n.read_at) markRead.mutate(n.id);
    if (n.subject_type === 'event') {
      navigate(`/?event=${n.subject_id}`);
    }
  };

  return (
    <Dropdown placement="bottom-end">
      <DropdownTrigger>
        <button
          className="relative p-1.5 rounded-md hover:bg-[var(--bg-panel)] transition-colors"
          style={{ color: 'var(--text-secondary)' }}
          aria-label="Notifications"
        >
          <Badge
            content={unread > 0 ? unread : undefined}
            color="danger"
            size="sm"
            isInvisible={unread === 0}
          >
            <Bell size={18} />
          </Badge>
        </button>
      </DropdownTrigger>
      <DropdownMenu
        aria-label="Notifications"
        className="max-h-96 overflow-y-auto w-80"
        style={{ backgroundColor: 'var(--bg-panel)' }}
        emptyContent={
          <div className="py-6 text-center text-xs font-ui" style={{ color: 'var(--text-muted)' }}>
            No notifications
          </div>
        }
        topContent={
          unread > 0 ? (
            <div className="flex justify-end px-2 py-1 border-b border-[var(--border)]">
              <Button
                size="sm"
                variant="light"
                startContent={<CheckCheck size={13} />}
                onPress={() => markAllRead.mutate()}
                className="text-xs font-ui"
              >
                Mark all read
              </Button>
            </div>
          ) : null
        }
        items={items}
      >
        {(n: NotificationDto) => (
          <DropdownItem
            key={n.id}
            textValue={n.preview}
            onPress={() => handleClick(n)}
            className={n.read_at ? '' : 'bg-[var(--accent)]/5'}
          >
            <div className="flex flex-col gap-0.5">
              <span className="text-xs font-ui" style={{ color: 'var(--text-primary)' }}>
                {n.preview}
              </span>
              <span className="text-[10px] font-ui" style={{ color: 'var(--text-muted)' }}>
                {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
              </span>
            </div>
          </DropdownItem>
        )}
      </DropdownMenu>
    </Dropdown>
  );
}
